import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { 
  Award, TrendingUp, BookOpen, CheckCircle, 
  Star, Trophy, Target, Users, Building2 
} from 'lucide-react';

const MyCertification = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // İlerleme bilgisi
      const progressRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/certifications/my-progress`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (progressRes.ok) {
        const data = await progressRes.json();
        setProgress(data);
      }

      // Gereksinimler
      const reqRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/certifications/requirements`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (reqRes.ok) {
        const data = await reqRes.json();
        setRequirements(data);
      }
    } catch (error) {
      console.error('Veri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimCertification = async () => {
    setClaiming(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/certifications/claim`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        loadData();
      } else {
        const err = await response.json();
        alert(err.detail || 'Sertifika alınamadı');
      }
    } catch (error) {
      console.error('Sertifika alma hatası:', error);
    } finally {
      setClaiming(false);
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Pro': return 'text-yellow-600 bg-yellow-100 border-yellow-300';
      case 'Medium': return 'text-blue-600 bg-blue-100 border-blue-300';
      case 'Basic': return 'text-green-600 bg-green-100 border-green-300';
      default: return 'text-gray-600 bg-gray-100 border-gray-300';
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'Pro': return '🏆';
      case 'Medium': return '🥈';
      case 'Basic': return '🥉';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb items={[{ label: 'Sertifikasyonum' }]} />
      
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">🏅 Öğretmen Sertifikasyonu</h1>
        <p className="text-gray-600">Kursları tamamlayarak sertifika seviyenizi yükseltin</p>
      </div>

      {/* Mevcut Sertifika */}
      {progress?.existingCertification ? (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 mb-6 border-2 border-yellow-200">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-white shadow-lg flex items-center justify-center text-5xl">
              {getLevelIcon(progress.existingCertification.level)}
            </div>
            <div className="flex-1">
              <p className="text-sm text-yellow-800 mb-1">Mevcut Sertifikanız</p>
              <h2 className="text-3xl font-bold text-gray-900">
                {progress.existingCertification.level} Seviye Öğretmen
              </h2>
              <p className="text-gray-600 mt-1">
                Kazanıldı: {new Date(progress.existingCertification.earnedAt).toLocaleDateString('tr-TR')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-yellow-600">{progress.existingCertification.totalPoints}</p>
              <p className="text-sm text-gray-500">Toplam Puan</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-xl p-6 mb-6 text-center">
          <Award size={48} className="mx-auto text-gray-400 mb-3" />
          <h2 className="text-xl font-semibold text-gray-700">Henüz Sertifikanız Yok</h2>
          <p className="text-gray-500">Kursları tamamlayarak sertifika kazanın</p>
        </div>
      )}

      {/* İlerleme Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<BookOpen className="text-blue-600" />}
          label="Zorunlu Kurslar"
          value={progress?.progress?.mandatoryCourses || 0}
          color="blue"
        />
        <StatCard
          icon={<BookOpen className="text-green-600" />}
          label="Seçmeli Kurslar"
          value={progress?.progress?.electiveCourses || 0}
          color="green"
        />
        <StatCard
          icon={<Target className="text-purple-600" />}
          label="Toplam Puan"
          value={progress?.progress?.totalPoints || 0}
          color="purple"
        />
        <StatCard
          icon={<TrendingUp className="text-orange-600" />}
          label="Quiz Ortalaması"
          value={`%${progress?.progress?.quizAverage || 0}`}
          color="orange"
        />
      </div>

      {/* Bir Sonraki Seviye */}
      {progress?.nextLevel && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Target className="text-blue-600" />
              Sonraki Seviye: {progress.nextLevel.level}
            </h3>
            {progress.eligibleForUpgrade && (
              <button
                onClick={handleClaimCertification}
                disabled={claiming}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Award size={18} />
                {claiming ? 'Alınıyor...' : 'Sertifikayı Al'}
              </button>
            )}
          </div>

          <div className="grid grid-cols-4 gap-4">
            <ProgressItem
              label="Zorunlu Kurslar"
              current={progress.progress.mandatoryCourses}
              required={progress.nextLevel.minMandatoryCourses}
            />
            <ProgressItem
              label="Seçmeli Kurslar"
              current={progress.progress.electiveCourses}
              required={progress.nextLevel.minElectiveCourses}
            />
            <ProgressItem
              label="Toplam Puan"
              current={progress.progress.totalPoints}
              required={progress.nextLevel.minTotalPoints}
            />
            <ProgressItem
              label="Quiz Ortalaması"
              current={progress.progress.quizAverage}
              required={progress.nextLevel.minQuizAverage}
              suffix="%"
            />
          </div>
        </div>
      )}

      {/* Seviye Gereksinimleri */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Star className="text-yellow-500" />
          Sertifika Seviyeleri
        </h3>

        <div className="grid md:grid-cols-3 gap-4">
          {requirements && Object.entries(requirements).map(([key, req]) => (
            <div 
              key={key}
              className={`p-4 rounded-lg border-2 ${getLevelColor(req.level)} ${
                progress?.currentLevel === req.level ? 'ring-2 ring-offset-2 ring-blue-500' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">{getLevelIcon(req.level)}</span>
                <div>
                  <h4 className="font-bold text-lg">{req.level}</h4>
                  {progress?.currentLevel === req.level && (
                    <span className="text-xs bg-white px-2 py-0.5 rounded-full">Mevcut</span>
                  )}
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Zorunlu Kurs:</span>
                  <span className="font-medium">{req.minMandatoryCourses}+</span>
                </div>
                <div className="flex justify-between">
                  <span>Seçmeli Kurs:</span>
                  <span className="font-medium">{req.minElectiveCourses}+</span>
                </div>
                <div className="flex justify-between">
                  <span>Toplam Puan:</span>
                  <span className="font-medium">{req.minTotalPoints}+</span>
                </div>
                <div className="flex justify-between">
                  <span>Quiz Ort.:</span>
                  <span className="font-medium">%{req.minQuizAverage}+</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// İstatistik Kartı
const StatCard = ({ icon, label, value, color }) => (
  <div className="bg-white rounded-xl shadow-sm p-4">
    <div className={`p-2 bg-${color}-100 rounded-lg w-fit mb-2`}>
      {icon}
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-sm text-gray-500">{label}</p>
  </div>
);

// İlerleme Öğesi
const ProgressItem = ({ label, current, required, suffix = '' }) => {
  const percentage = Math.min(100, (current / required) * 100);
  const isComplete = current >= required;

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className={isComplete ? 'text-green-600 font-medium' : 'text-gray-900'}>
          {current}{suffix} / {required}{suffix}
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {isComplete && (
        <div className="flex items-center gap-1 text-green-600 text-xs mt-1">
          <CheckCircle size={12} />
          Tamamlandı
        </div>
      )}
    </div>
  );
};

export default MyCertification;
